import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePrimaryComplaintStore, VisitReason } from "@/store/primaryComplaint";
import { useTaskStore } from "@/store/task";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { ComplaintStepHeader, StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { useComplaintValues } from "@/hooks/useComplaintValues";
import { resolveComplaintType } from "@/utils/complaintSteps";

const FactorFields = {
  RELIEF: "Relief",
  AGGS: "Aggs",
  STORY: "Story",
} as const;

const PAIN_BETTER = [
  "rest",
  "ice",
  "heat",
  "stretching",
  "exercise",
  "medication",
  "massage",
  "changeInPosition",
  "nothingMakesItBetter",
] as const;

const PAIN_WORSE = [
  "movement",
  "standing",
  "sitting",
  "walking",
  "lifting",
  "bending",
  "twisting",
  "physicalActivity",
  "nothingMakesItWorse",
] as const;

const NUMBNESS_BETTER = [
  "movement",
  "massage",
  "stretching",
  "changeInPosition",
  "nothingMakesItBetter",
] as const;

const NUMBNESS_WORSE = [
  "sitting",
  "standing",
  "pressure",
  "physicalActivity",
  "nothingMakesItWorse",
] as const;

const WEAKNESS_BETTER = [
  "rest",
  "exercise",
  "stretching",
  "changeInPosition",
  "nothingMakesItBetter",
] as const;

const WEAKNESS_WORSE = [
  "physicalActivity",
  "standing",
  "walking",
  "lifting",
  "nothingMakesItWorse",
] as const;

const FACTORS_BY_REASON: Partial<
  Record<
    VisitReason,
    { better: readonly string[]; worse: readonly string[] }
  >
> = {
  [VisitReason.PAIN]: { better: PAIN_BETTER, worse: PAIN_WORSE },
  [VisitReason.NUMBNESS]: { better: NUMBNESS_BETTER, worse: NUMBNESS_WORSE },
  [VisitReason.WEAKNESS]: { better: WEAKNESS_BETTER, worse: WEAKNESS_WORSE },
};

function getFactorLabel(key: string): string {
  const k = key as keyof typeof t;
  return (t[k] as string) ?? key;
}

function getVisitReasonLabel(reason: string | null): string {
  if (!reason) return "";
  const key = reason as keyof typeof t;
  return (t[key] as string) ?? reason;
}

const NOTHING_KEYS = ["nothingMakesItBetter", "nothingMakesItWorse"];

/** "Nothing" excludes every other factor: picking it clears the rest, picking another clears it. */
function applyNothingRule(current: string[], next: string[]): string[] {
  const added = next.find((v) => !current.includes(v));
  if (added && NOTHING_KEYS.includes(added)) return [added];
  return next.filter((v) => !NOTHING_KEYS.includes(v));
}

export function ComplaintFactorsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get("complaintId");
  const patientID = searchParams.get("patientID");
  const caseHistoryIdParam = searchParams.get("caseHistoryId");

  const complaintHeaders = usePrimaryComplaintStore((s) => s.complaintHeaders);
  const visitReason = usePrimaryComplaintStore((s) => s.visitReason);
  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);
  const taskCaseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const caseHistoryId = caseHistoryIdParam ?? (taskCaseHistoryId ? String(taskCaseHistoryId) : null);
  const { values, status, retry } = useComplaintValues(complaintId);

  const [relief, setRelief] = useState<string[]>([]);
  const [aggs, setAggs] = useState<string[]>([]);
  const [story, setStory] = useState("");
  // Fields the patient changed on this visit; Continue re-sends them even when now empty.
  const touched = useRef(new Set<string>());
  const [isSaving, setIsSaving] = useState(false);

  const complaintType = resolveComplaintType(complaintHeaders, complaintId, visitReason);
  const factorsConfig = complaintType ? FACTORS_BY_REASON[complaintType as VisitReason] : null;
  const betterOptions = factorsConfig?.better ?? [];
  const worseOptions = factorsConfig?.worse ?? [];
  const hasFactors = betterOptions.length > 0 || worseOptions.length > 0;

  useEffect(() => {
    if (status !== "ready" || !values) return;
    for (const v of values) {
      if (v.code === FactorFields.RELIEF) setRelief(v.value ? v.value.split(",").filter(Boolean) : []);
      else if (v.code === FactorFields.AGGS) setAggs(v.value ? v.value.split(",").filter(Boolean) : []);
      else if (v.code === FactorFields.STORY) setStory(v.value ?? "");
    }
  }, [status, values]);

  const changeRelief = (next: string[]) => {
    const value = applyNothingRule(relief, next);
    setRelief(value);
    touched.current.add(FactorFields.RELIEF);
    void updateComplaintValue(FactorFields.RELIEF, value.join(",")).catch(() => {});
  };

  const changeAggs = (next: string[]) => {
    const value = applyNothingRule(aggs, next);
    setAggs(value);
    touched.current.add(FactorFields.AGGS);
    void updateComplaintValue(FactorFields.AGGS, value.join(",")).catch(() => {});
  };

  // Track the story locally as they type; persist once they pause (blur). Saving on every keystroke
  // fires an overlapping request per character, which can arrive out of order and overwrite the final
  // text with an earlier partial value. Continue re-saves it (even when cleared) as a safety net.
  const handleStoryBlur = () => {
    void updateComplaintValue(FactorFields.STORY, story).catch(() => {});
  };

  const goBack = () => {
    const search = caseHistoryId
      ? `?complaintId=${complaintId}&caseHistoryId=${caseHistoryId}`
      : `?complaintId=${complaintId}&patientID=${patientID ?? ""}`;
    navigate({ pathname: "/complaint/complaint-history", search });
  };

  const saveAndContinue = async () => {
    // Only once saved answers have loaded; otherwise the form may not reflect what's saved and
    // re-saving would overwrite it. Re-save every answered field, plus every field changed on this
    // visit even when now empty (a clear whose immediate save failed must not stay), and wait.
    if (status !== "ready") return;
    const saves: Promise<unknown>[] = [];
    const resave = (code: string, value: string) => {
      if (value.trim() !== "" || touched.current.has(code)) saves.push(updateComplaintValue(code, value));
    };
    resave(FactorFields.RELIEF, relief.join(","));
    resave(FactorFields.AGGS, aggs.join(","));
    resave(FactorFields.STORY, story);
    setIsSaving(true);
    try {
      await Promise.all(saves);
    } catch {
      // updateComplaintValue already surfaces a toast on failure; don't advance so the patient
      // can retry without losing what they typed.
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    const search = new URLSearchParams();
    search.set("complaintId", complaintId ?? "");
    if (caseHistoryId) {
      search.set("caseHistoryId", caseHistoryId);
    } else {
      search.set("patientID", patientID ?? "");
    }
    navigate({ pathname: "/complaint/outcome-assesment", search: `?${search.toString()}` });
  };

  if (!complaintId || (!patientID && !caseHistoryId)) {
    return (
      <div className="wizard-page w-full">
        <div className="wizard-page__content p-4">
          <div className="complaint-history-card">
            <p className="text-sm text-healthsource-gray">Missing complaint or patient. Go back to complaints.</p>
          </div>
        </div>
        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={() => navigate("/complaint/primary-complaint")}>
            ← {t.backButton}
          </Button>
        </WizardActionsBar>
      </div>
    );
  }

  const reasonLabel = getVisitReasonLabel(complaintType).toLowerCase() || "complaint";

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintId} step="factors" heading={t.complaintFactors} />

          {status === "loading" && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your answers">
              <span />
              <span />
              <span />
            </div>
          )}

          {status === "error" && <StepLoadError onRetry={retry} />}

          {status === "ready" && (
            <>
              {hasFactors && (
                <>
                  <ChoiceGroup
                    id="relief"
                    multiple
                    legend={t.whatMakesItBetter}
                    hint={`(${t.selectAllApply})`}
                    options={betterOptions.map((o) => ({ value: o, label: getFactorLabel(o) }))}
                    value={relief}
                    onChange={changeRelief}
                  />
                  <ChoiceGroup
                    id="aggs"
                    multiple
                    legend={t.whatMakesItWorse}
                    hint={`(${t.selectAllApply})`}
                    options={worseOptions.map((o) => ({ value: o, label: getFactorLabel(o) }))}
                    value={aggs}
                    onChange={changeAggs}
                  />
                </>
              )}

              <div className="choice-group">
                <label htmlFor="complaint-factors-story" className="choice-textarea-label">
                  {t.additionalInfo} <span className="choice-group__hint">{t.optional}</span>
                </label>
                <textarea
                  id="complaint-factors-story"
                  className="complaint-history-textarea"
                  placeholder={`Is there anything else you would like us to know about your ${reasonLabel}?`}
                  value={story}
                  onChange={(e) => {
                    setStory(e.target.value);
                    touched.current.add(FactorFields.STORY);
                  }}
                  onBlur={handleStoryBlur}
                  rows={3}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue} disabled={isSaving || status !== "ready"}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
